import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { Shield, ArrowLeft, Key } from 'lucide-react';
import { useToast } from '@/components/ui/use-toast';

interface TwoFactorLoginProps {
  onVerify: (code: string, useBackupCode?: boolean) => Promise<any>;
  onGoBack: () => void;
  isLoading?: boolean;
}

export const TwoFactorLogin: React.FC<TwoFactorLoginProps> = ({
  onVerify,
  onGoBack,
  isLoading = false,
}) => {
  const [code, setCode] = useState('');
  const [useBackupCode, setUseBackupCode] = useState(false);
  const { toast } = useToast();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (code.length < 6) {
      toast({
        variant: "destructive",
        title: "Invalid Code",
        description: "Please enter a valid 6-digit code",
      });
      return;
    }

    try {
      await onVerify(code, useBackupCode);
    } catch (error) {
      // Error handling is done in the parent component
    }
  };

  const toggleBackupCode = () => {
    setUseBackupCode(!useBackupCode);
    setCode('');
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-gray-50" data-testid="two-factor-login-page">
      <Card className="w-full max-w-md">
        <CardHeader className="space-y-1">
          <CardTitle className="flex items-center gap-2 text-xl">
            <Shield className="h-5 w-5" />
            Two-Factor Authentication
          </CardTitle>
          <CardDescription>
            {useBackupCode 
              ? "Enter one of your backup codes to continue"
              : "Enter the 6-digit code from your authenticator app"
            }
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="auth-code">
                {useBackupCode ? "Backup Code" : "Authentication Code"}
              </Label>
              <Input
                id="auth-code"
                type="text"
                placeholder={useBackupCode ? "Enter backup code" : "000000"}
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 8))}
                className={`text-center font-mono text-lg ${useBackupCode ? '' : 'tracking-widest'}`}
                data-testid="auth-code-input"
                autoComplete="off"
                autoFocus
              />
            </div>

            <Button
              type="submit"
              className="w-full"
              disabled={code.length < 6 || isLoading}
              data-testid="verify-code-button"
            >
              {isLoading ? 'Verifying...' : 'Verify'}
            </Button>
          </form>

          <Separator />

          <div className="space-y-3">
            <Button
              type="button"
              variant="outline"
              onClick={toggleBackupCode}
              className="w-full"
              data-testid="toggle-backup-code-button"
            >
              <Key className="h-4 w-4 mr-2" />
              {useBackupCode 
                ? "Use Authenticator App" 
                : "Use Backup Code"
              }
            </Button>

            <Button
              type="button"
              variant="ghost"
              onClick={onGoBack}
              className="w-full"
              data-testid="go-back-button"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Login
            </Button>
          </div>

          <div className="text-center">
            <p className="text-sm text-muted-foreground">
              {useBackupCode 
                ? "Each backup code can only be used once"
                : "Need help? Use a backup code or contact support"
              }
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};